import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Banknote, Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import { Slider } from "./ui/slider";
import { saveEstimate, loadEstimateInputs } from "../lib/savings";
import { useQuoteModal } from "../lib/quoteModal";
import { track, events } from "../lib/analytics";
import useInView from "../lib/useInView";
import CountUp from "./CountUp";
import CalculatorPlaybookWall from "./CalculatorPlaybookWall";
import { CTA_SAVINGS_CALCULATOR } from "../lib/ctaVocabulary";
import { fireAudienceCalculatorEngaged } from "../lib/audienceSeeds";

// Iter-112 — Premium Live Estimate refinement.
// Math, logic, and conversion flow preserved from iter-80 baseline.
// What changed: premium slider styling (`.savings-slider` in index.css),
// animated CountUp for the headline + breakdown values, segmented
// breakdown pills with mini-bars, contextual insight strip, context-aware
// CTA label, ambient radial glow behind the savings card, methodology
// trust line.
//
// Iter-113 — Sliders rehydrate from the visitor's prior session via
// `loadEstimateInputs()`. A subtle "Restored from your last visit"
// badge appears for 4 seconds when hydration succeeds, with a reset
// affordance to clear the restored state in one click.

const DEFAULTS = { premium: 180, loan: 22000, rate: 9 };

export default function SavingsCalculator() {
  const { open: openQuoteModal } = useQuoteModal();
  // Iter-113 — Hydrate from localStorage on first render. If the visitor
  // dragged sliders before (and we persisted via saveEstimate), restore
  // their last position. Otherwise fall back to the editorial defaults.
  const initialInputs = useMemo(() => loadEstimateInputs() || DEFAULTS, []);
  const wasHydrated = useMemo(
    () =>
      initialInputs.premium !== DEFAULTS.premium ||
      initialInputs.loan !== DEFAULTS.loan ||
      initialInputs.rate !== DEFAULTS.rate,
    [initialInputs]
  );
  const [premium, setPremium] = useState(initialInputs.premium);
  const [loan, setLoan] = useState(initialInputs.loan);
  const [rate, setRate] = useState(initialInputs.rate);
  // "Restored from your last visit" pill — auto-dismisses after 4s so
  // it never lingers as visual noise.
  const [showHydratedPill, setShowHydratedPill] = useState(wasHydrated);
  const hydrationLogged = useRef(false);
  // Iter-190.G — `playbookDismissed` un-mounts the entire row-4
  // playbook block when the user clicks "Skip for now". Previously
  // Skip was a no-op visually (only logged analytics). Now the
  // container shrinks away, matching user expectation.
  const [playbookDismissed, setPlaybookDismissed] = useState(false);
  // Iter-160 (Phase-4 follow-up) — Savings card scroll-into-view entrance.
  // Drives the .savings-card-enter animation. One-shot — reduced-motion
  // safe via the useInView hook's own preference check.
  const { ref: savingsCardRef, hasEntered: savingsCardInView } = useInView({
    rootMargin: "0px 0px -12% 0px",
    threshold: 0.25,
  });

  // Annual savings only — both numbers represent ONE year's reduction, so they
  // can be added directly. Refi savings is annualized below by dividing the
  // 48-month total by 4.
  const insuranceAnnualSavings = useMemo(
    // 18% is the realistic median savings when a driver re-quotes 4+ insurers
    // (AAA + Bankrate consumer surveys). Capped at $2,200/yr to avoid absurd
    // numbers at slider extremes.
    () => Math.min(2200, Math.round(premium * 12 * 0.18)),
    [premium]
  );
  const refiSavings = useMemo(() => {
    const months = 48;
    // Realistic median refi drop is 2.5 percentage points (not 3) based on
    // 600–740 FICO band post-refi data. Floor at 4% APR.
    const newRate = Math.max(rate - 2.5, 4) / 100 / 12;
    const oldRate = rate / 100 / 12;
    const pmt = (p, r, n) => (p * r) / (1 - Math.pow(1 + r, -n));
    const oldPay = pmt(loan, oldRate, months);
    const newPay = pmt(loan, newRate, months);
    // (oldPay - newPay) is per-month; multiplying by 12 gives the ANNUAL refi
    // savings — consistent with the insurance number above.
    const annual = Math.max(0, (oldPay - newPay) * 12);
    return Math.min(2400, Math.round(annual));
  }, [loan, rate]);

  const total = insuranceAnnualSavings + refiSavings;

  // Iter-112 — Contextual insight strip. Surfaces editorial micro-copy
  // tied to the current slider position. Picks the most informative
  // signal (premium > $220 or APR ≥ 8% are the strongest re-shop windows
  // per CFPB / Bankrate consumer data).
  const insight = useMemo(() => getInsight({ premium, loan, rate }), [
    premium,
    loan,
    rate,
  ]);

  // Iter-112 — Context-aware CTA. When one savings lane dominates the
  // other by 25%+, surface the dominant lane's CTA. When they're
  // roughly even, use the generic "Get matched offers" verb.
  const ctaContext = useMemo(
    () => getCtaContext({ insuranceAnnualSavings, refiSavings, total }),
    [insuranceAnnualSavings, refiSavings, total]
  );

  // Persist the estimate so the footer can re-engage the visitor next time.
  // Debounced — only commit after the user stops dragging for 700ms.
  // Iter-113 — Also persists the slider inputs so the calculator can
  // rehydrate the visitor's last position on return visits.
  useEffect(() => {
    if (!total || total <= 0) return;
    // Iter-185.BM (AUDIT #10) — Fire the PostHog "calculator_engaged_no_quote"
    // audience anchor once the user has actually moved a slider and we
    // have a meaningful estimate. sessionStorage de-dupes inside
    // audienceSeeds.js.
    fireAudienceCalculatorEngaged({
      calculator_type: "home-savings-slider",
      estimated_annual: total,
      page_location: typeof window !== "undefined" ? window.location.pathname : "/",
    });
    const t = setTimeout(() => {
      saveEstimate({
        type: "combined",
        annual_savings: total,
        source: "hero-savings-calculator",
        inputs: { premium, loan, rate },
      });
    }, 700);
    return () => clearTimeout(t);
  }, [total, premium, loan, rate]);

  // Iter-113 — Auto-dismiss the "Restored from your last visit" pill
  // after 4s + log a one-time analytics event so we can measure
  // return-visit hydration rate.
  useEffect(() => {
    if (!wasHydrated) return;
    if (!hydrationLogged.current) {
      track(events.SAVINGS_ESTIMATE_HYDRATED, {
        premium: initialInputs.premium,
        loan: initialInputs.loan,
        rate: initialInputs.rate,
      });
      hydrationLogged.current = true;
    }
    const t = setTimeout(() => setShowHydratedPill(false), 4000);
    return () => clearTimeout(t);
  }, [wasHydrated, initialInputs]);

  const handleResetHydration = () => {
    setPremium(DEFAULTS.premium);
    setLoan(DEFAULTS.loan);
    setRate(DEFAULTS.rate);
    setShowHydratedPill(false);
  };

  // Iter-116 — Reveal-state for the inline methodology accordion. The
  // "Our math" link below the right column toggles it open/closed
  // instead of bouncing the visitor off to /methodology.
  const [showMethodology, setShowMethodology] = useState(false);

  return (
    <section
      id="calculator"
      data-testid="savings-calculator"
      data-suppress-floating-proof
      className="relative bg-slate-950 text-white py-20 lg:py-24 overflow-hidden"
    >
      {/* Layer 1 — base grid (iter-80) + iter-146 ambient drift.
          The grid pattern slowly drifts diagonally over 60s so the
          section reads as "alive" without ever distracting from the
          slider math. Reduced-motion safe. */}
      <div
        data-testid="savings-grid-layer"
        className="absolute inset-0 opacity-[0.06] savings-grid-drift"
        style={{
          backgroundImage:
            "linear-gradient(to right, #10B981 1px, transparent 1px), linear-gradient(to bottom, #10B981 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden
      />
      {/* Layer 2 — ambient radial glow (iter-112) gives depth without animation */}
      <div className="absolute inset-0 savings-ambient-glow" aria-hidden />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Iter-190.F — Live Estimate desktop reflow.
            User feedback: the previous 2×2 grid (header+card in left col,
            sliders+CTA in right col) left ~50% of the right column empty
            below the slider stack. Redesigned to a proper 4-row flow:
              Row 1: Section header   — full-width (col-span-12)
              Row 2: Sliders          — col-span-7 (left)
                     Savings card     — col-span-5 (right, row-span-2 so
                                        it aligns with sliders + CTA)
              Row 3: Compare CTA      — col-span-7 (under sliders)
              Row 4: Playbook wall    — full-width (col-span-12) — fixes
                                        the user-reported "skinny email
                                        input on mobile" because the wall
                                        is no longer constrained to the
                                        narrow col-5 left column.
            Mobile flow preserved in editorial reading order via
            `order-1..order-5`. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-8 lg:gap-y-6 lg:items-start">

          {/* BLOCK 1 — Section header (full-width on desktop). */}
          <div
            data-testid="savings-section-header"
            className="order-1 lg:order-none lg:col-span-12 lg:row-start-1 max-w-2xl mb-2 lg:mb-4"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#10B981] mb-4">
              <span className="inline-block bg-[#10B981]/15 text-[#10B981] px-2 py-1">Live estimate</span>
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.0] mb-5">
              See where your car costs could come down.
            </h2>
            <p className="text-slate-300 text-base leading-relaxed max-w-md lg:mb-2">
              Adjust the sliders to estimate potential savings on insurance and
              loan payments — in seconds.
            </p>
          </div>

          {/* BLOCK 2 — Your Inputs sliders. Mobile second; desktop row-2 left. */}
          <div
            data-testid="savings-inputs-block"
            className="order-2 lg:order-none lg:col-span-7 lg:row-start-2"
          >
            {/* Iter-115 — Right-column eyebrow mirrors the LIVE ESTIMATE
                eyebrow on the left. Creates immediate visual symmetry and
                anchors the slider zone at the top edge of the column,
                eliminating the cold-start feel where sliders previously
                began without context. */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                <span className="text-[#10B981]">●</span> Your inputs
              </p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Drag any slider · totals refresh live
              </p>
            </div>

            <div className="space-y-6">
              <SliderRow
                testId="slider-premium"
                label="Current monthly insurance premium"
                value={premium}
                display={`$${premium}/mo`}
                min={50}
                max={500}
                step={5}
                onChange={(v) => setPremium(v[0])}
              />
              <SliderRow
                testId="slider-loan"
                label="Auto loan balance"
                value={loan}
                display={`$${loan.toLocaleString()}`}
                min={3000}
                max={70000}
                step={500}
                onChange={(v) => setLoan(v[0])}
              />
              <SliderRow
                testId="slider-rate"
                label="Current loan APR"
                value={rate}
                display={`${rate.toFixed(1)}%`}
                min={3}
                max={18}
                step={0.5}
                onChange={(v) => setRate(v[0])}
              />
            </div>
          </div>

          {/* BLOCK 3 — Savings result card. Mobile third; desktop row-2 right.
              `lg:row-span-2` lets the card extend down to row-3 so its
              bottom edge naturally aligns with the CTA strip in the left
              column — fixes the iter-190.F "empty right column" finding. */}
          <div
            data-testid="savings-card-block"
            className="order-3 lg:order-none lg:col-span-5 lg:row-start-2 lg:row-span-2"
          >
            {/* Iter-112 — Savings card refined.
                Iter-146 — `savings-card-lift` adds subtle -2px hover
                lift + emerald-tinted shadow grow so the card reads as
                "interactable" the moment a cursor enters the section.
                Reduced-motion safe. */}
            <div
              ref={savingsCardRef}
              data-testid="savings-card"
              className={`savings-card-lift relative border border-white/15 bg-white/[0.04] backdrop-blur-sm p-7 md:p-8 ${
                savingsCardInView ? "savings-card-enter" : "opacity-0"
              }`}
            >
              {/* Iter-113 — Restored-from-last-visit pill, auto-dismiss after 4s */}
              {showHydratedPill && (
                <div
                  data-testid="savings-hydrated-pill"
                  className="absolute -top-3 right-4 inline-flex items-center gap-1.5 bg-[#10B981] text-slate-950 text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 shadow-lg"
                >
                  <RotateCcw className="w-3 h-3" strokeWidth={2.5} />
                  <span>Restored from your last visit</span>
                  <button
                    type="button"
                    onClick={handleResetHydration}
                    data-testid="savings-hydrated-reset"
                    className="ml-1 text-slate-950/60 hover:text-slate-950 font-bold underline-offset-2 hover:underline normal-case tracking-normal"
                  >
                    Reset
                  </button>
                </div>
              )}
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3 flex items-center gap-2">
                <span>Estimated annual savings</span>
              </div>
              <CountUp
                value={total}
                testid="savings-total"
                className={`font-display font-black text-6xl md:text-7xl text-[#10B981] leading-none tracking-tighter block ${
                  total > 1500 ? "savings-headline-pulse" : ""
                }`}
                duration={500}
              />

              {/* Iter-112 — Breakdown pills replace the plain "Insurance + Refi" text */}
              <div
                data-testid="savings-breakdown"
                className="mt-6 space-y-3"
              >
                <BreakdownPill
                  testid="savings-breakdown-insurance"
                  icon={ShieldCheck}
                  label="Insurance"
                  value={insuranceAnnualSavings}
                  total={total}
                />
                <BreakdownPill
                  testid="savings-breakdown-refi"
                  icon={Banknote}
                  label="Refinance"
                  value={refiSavings}
                  total={total}
                />
              </div>

              {total > 0 && (
                <div className="mt-6 pt-5 border-t border-white/10">
                  {/* Iter-112 — Dynamic insight strip */}
                  <div
                    data-testid="savings-insight"
                    className="flex items-start gap-2.5 text-sm text-slate-200 leading-relaxed"
                  >
                    <Sparkles className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                    <span>
                      <span className="text-[#10B981] font-bold">
                        {insight.lede}
                      </span>{" "}
                      {insight.body}
                    </span>
                  </div>
                </div>
              )}

              {/* Iter-112 — Trust + methodology footer */}
              <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <span data-testid="savings-methodology-note">
                  Based on national averages · Updated quarterly
                </span>
                <Link
                  to="/methodology"
                  data-testid="savings-methodology-link"
                  className="text-slate-300 hover:text-[#10B981] underline-offset-4 hover:underline transition-colors"
                >
                  How we estimate →
                </Link>
              </div>
            </div>
            {/* Iter-190.F — Playbook wall moved to its own row-4
                col-span-12 block (BLOCK 5 below). Keeps the savings
                card alone in the right column AND gives the email
                input proper width on both desktop and mobile. */}
          </div>

          {/* BLOCK 4 — Compare CTA + trust strip + methodology bridge.
              Mobile fourth; desktop row-3 left (under the sliders).
              Iter-190.F: moved from row-2 right to row-3 left so the
              primary action sits directly beneath the sliders the user
              just dragged — natural reading flow. */}
          <div
            data-testid="savings-cta-block"
            className="order-4 lg:order-none lg:col-span-7 lg:row-start-3 flex flex-col"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
              <button
                type="button"
                onClick={() => {
                  track(events.CALCULATOR_RESULT_CTA_CLICK, {
                    calculator_type: "home-savings-slider",
                    page_location: "/",
                    estimated_annual: total,
                    monthly_premium: premium,
                    loan_balance: loan,
                    current_apr: rate,
                    cta_context: ctaContext.key,
                  });
                  const personalized = total > 0
                    ? {
                        headline: `Your projected savings: $${total.toLocaleString()}/yr.`,
                        sub: `Insurance $${insuranceAnnualSavings.toLocaleString()} + refi $${refiSavings.toLocaleString()}. Get your matched offers in about 2 minutes.`,
                        successContext: {
                          kind: "insurance",
                          estimated_annual: insuranceAnnualSavings * 5,
                        },
                      }
                    : {};
                  openQuoteModal(ctaContext.category, personalized);
                }}
                data-testid="calculator-cta"
                className="lift press inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-slate-950 font-bold h-14 px-8 group shrink-0"
              >
                <span data-testid="calculator-cta-label">{ctaContext.label}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p
                data-testid="calculator-trust"
                className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 leading-relaxed"
              >
                Free · 2 min · Soft credit pull · No spam
              </p>
            </div>

            {/* Iter-115 — Bottom bridge anchor that visually mirrors the
                savings-card methodology footer on the left column. Pushed
                to the bottom via `mt-auto` so the right column bottoms-out
                at the same Y position as the left card footer.
                Iter-116 — "Our math" is now an in-page accordion toggle
                instead of a /methodology bounce. */}
            <div className="mt-auto pt-8 hidden lg:flex items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 border-t border-white/5">
              <span data-testid="calculator-right-anchor">
                Estimates update instantly · No data leaves your browser
              </span>
              <button
                type="button"
                onClick={() => {
                  const next = !showMethodology;
                  setShowMethodology(next);
                  if (next) {
                    track(events.METHODOLOGY_PANEL_OPENED, {
                      source: "live-estimate-bridge-anchor",
                    });
                  }
                }}
                data-testid="methodology-toggle"
                aria-expanded={showMethodology}
                aria-controls="methodology-panel"
                className="text-slate-300 hover:text-[#10B981] underline-offset-4 hover:underline transition-colors normal-case tracking-[0.18em]"
              >
                {showMethodology ? "Hide our math ↑" : "Our math →"}
              </button>
            </div>
          </div>

          {/* BLOCK 5 — Personalized Savings Playbook (Iter-190.F).
              Full-width row below the live calculator. Moves the email
              capture wall out of the cramped col-5 left column where
              the email input read as a "skinny" sliver on mobile + lg
              viewports. Now the wall + email input get the full
              container width on desktop AND mobile. Email capture is
              the secondary action (calculator CTA is primary) so it
              sits BELOW the primary action in the reading order.
              Iter-190.G: gated by !playbookDismissed so the "Skip
              for now" button can shrink the entire row away. */}
          {total > 0 && !playbookDismissed && (
            <div
              data-testid="savings-playbook-block"
              className="order-5 lg:order-none lg:col-span-12 lg:row-start-4 mt-2 lg:mt-6"
            >
              <CalculatorPlaybookWall
                category={ctaContext.key === "refinance" ? "refinance" : ctaContext.key === "insurance" ? "insurance" : "combined"}
                estimatedAnnual={total}
                sourcePage="home-savings-calculator"
                variant="dark"
                onSkip={() => setPlaybookDismissed(true)}
              />
            </div>
          )}
        </div>

        {/* Iter-116 — Inline methodology accordion. Pure CSS height
            transition (max-h + opacity); reduced-motion safe. Shows the
            actual formulas + data sources backing the slider math so
            users get editorial transparency without bouncing off to
            /methodology. */}
        {showMethodology && (
          <div
            id="methodology-panel"
            data-testid="methodology-panel"
            className="mt-10 hidden lg:block border-t border-white/10 pt-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MethodologyCard
                eyebrow="Insurance"
                formula="Premium × 12 × 18%"
                explainer="The median driver who quotes 4+ insurers saves 15–22% off their renewal premium. We use 18% — the midpoint of the AAA + Bankrate consumer survey ranges — and cap projected annual savings at $2,200."
                sources={[
                  { label: "AAA insurance shopping survey", year: 2024 },
                  { label: "Bankrate annual rate report", year: 2025 },
                ]}
              />
              <MethodologyCard
                eyebrow="Refinance"
                formula="(Old Payment − New Payment) × 12"
                explainer={`We compare your current APR against a target rate set 2.5 percentage points lower (floored at 4% APR — the realistic prime-band floor right now). Payments are computed over the remaining 48 months of your loan. Annual savings capped at $2,400.`}
                sources={[
                  { label: "Federal Reserve G.19 consumer credit", year: 2025 },
                  { label: "Experian State of the Automotive Finance", year: 2025 },
                ]}
              />
            </div>
            <p className="mt-5 text-[11px] text-slate-500 leading-relaxed max-w-2xl">
              Estimates are educational projections — not quotes. Your actual savings
              depend on driving history, credit profile, loan terms, and the lender or
              carrier you ultimately choose. <Link to="/methodology" className="underline underline-offset-4 hover:text-[#10B981]">Full methodology →</Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// Iter-116 — Methodology accordion card. Eyebrow + bold formula
// (tabular-nums for symmetry) + plain-English explainer + provenance
// chips. Visual language matches the savings card / breakdown pills so
// the reveal feels integrated, not bolted on.
function MethodologyCard({ eyebrow, formula, explainer, sources }) {
  return (
    <div
      data-testid={`methodology-card-${eyebrow.toLowerCase()}`}
      className="border border-white/15 bg-white/[0.04] backdrop-blur-sm p-6"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981] mb-2">
        {eyebrow}
      </p>
      <p className="font-mono text-sm text-white tracking-tight mb-3 tabular-nums">
        {formula}
      </p>
      <p className="text-sm text-slate-300 leading-relaxed mb-4">{explainer}</p>
      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
        {sources.map((src) => (
          <span
            key={src.label}
            className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300"
          >
            <span>{src.label}</span>
            <span className="text-slate-500">{src.year}</span>
          </span>
        ))}
      </div>
    </div>
  );
}


// Iter-112 — Breakdown pill: icon + label on the left, animated value on
// the right, mini progress bar underneath showing this lane's share of
// the total. Pure CSS bar, GPU-only width transition (`.savings-mini-bar`).
function BreakdownPill({ icon: Icon, label, value, total, testid }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div
      data-testid={testid}
      className="bg-white/[0.03] border border-white/10 p-3.5 transition-colors hover:border-white/20"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 grid place-items-center bg-[#10B981]/15 text-[#10B981] shrink-0">
            <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
          </div>
          <span className="text-sm font-semibold text-slate-200 truncate">
            {label}
          </span>
        </div>
        <CountUp
          value={value}
          duration={400}
          className="font-display font-black text-lg text-white tabular-nums shrink-0"
        />
      </div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <div
          className="savings-mini-bar"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

function SliderRow({ label, value, display, min, max, step, onChange, testId, welcome = false }) {
  // Iter-146 — `welcome` opts THIS slider into a one-shot 1.5s shimmer
  // pass on mount that invites first-time visitors to drag. Pure CSS,
  // GPU-only, reduced-motion safe (the @media rule disables it).
  // Wrapped in a `relative` container so the absolutely-positioned
  // ::after pseudo-element can paint over the track without affecting
  // layout. Only the premium slider passes `welcome` so the section
  // doesn't feel "all flashing at once" on first paint.
  return (
    <div className="border-b border-white/10 pb-6 group">
      <div className="flex items-center justify-between mb-4">
        <label
          htmlFor={testId}
          className="text-sm font-semibold text-slate-300 group-hover:text-slate-100 transition-colors"
        >
          {label}
        </label>
        <span
          key={value}
          className="savings-display-pulse font-display font-black text-2xl md:text-3xl text-white tracking-tight tabular-nums inline-block"
          data-testid={`${testId}-display`}
        >
          {display}
        </span>
      </div>
      <div className={welcome ? "relative savings-slider-welcome" : "relative"}>
        <Slider
          data-testid={testId}
          id={testId}
          value={[value]}
          min={min}
          max={max}
          step={step}
          onValueChange={onChange}
          // Iter-275.Z (a11y) — forward an accessible name to the Radix
          // Slider.Thumb so SR users hear what they're adjusting.
          aria-label={label}
          className="savings-slider"
        />
      </div>
    </div>
  );
}

// -------- Helpers --------

// Iter-112 — Insight selector. Picks the dominant editorial cue based on
// the user's current slider values. NO fake personalization — every line
// is general-purpose data interpretation backed by industry surveys.
function getInsight({ premium, loan, rate }) {
  if (rate >= 9 && loan >= 15000) {
    return {
      lede: "Strong refinance opportunity.",
      body:
        "APRs at or above 9% on a balance this size sit in the strongest refinance band — most borrowers cut their rate by 2 percentage points or more.",
    };
  }
  if (premium >= 220) {
    return {
      lede: "Good time to re-shop.",
      body:
        "Premiums above $220/mo have the largest re-quote gap — drivers who re-shop 4+ insurers typically save 15–22%.",
    };
  }
  if (rate >= 8) {
    return {
      lede: "Above-market APR.",
      body:
        "Most lenders price prime credit at ≤6% APR right now — a soft-pull check tells you where you stand without dinging your score.",
    };
  }
  if (loan >= 35000) {
    return {
      lede: "Balance amplifies savings.",
      body:
        "On loans of $35k+, even a 1.5-point APR drop compounds into meaningful annual savings over the next 4 years.",
    };
  }
  return {
    lede: "Worth a 2-minute check.",
    body:
      "Most drivers haven't re-shopped insurance in 3+ years — that's where the savings hide. A soft-pull comparison is free and won't affect your score.",
  };
}

// Iter-112 — Context-aware CTA. Surfaces the dominant lane's verb when
// one savings stream is materially larger; otherwise generic.
//
// Iter-185.BI — Aligned to the user-preferred vocabulary set so the
// calculator's primary CTA speaks the same verb as the homepage hero
// ZIP-form + every modal intake.
// Iter-190.G — The Live Estimate calculator covers BOTH insurance
// AND loan savings, so the CTA must use the cross-vertical
// "Compare Offers" label regardless of which lane currently has
// the higher savings share. We still keep the `category` field
// dynamic — it routes the quote modal to whichever lane is
// dominant — but the visible label no longer flips mid-session.
function getCtaContext({ insuranceAnnualSavings, refiSavings, total }) {
  if (total <= 0) {
    return {
      key: "neutral",
      label: CTA_SAVINGS_CALCULATOR,
      category: "insurance",
    };
  }
  const insShare = insuranceAnnualSavings / total;
  if (insShare >= 0.62) {
    return {
      key: "insurance",
      label: CTA_SAVINGS_CALCULATOR,
      category: "insurance",
    };
  }
  if (insShare <= 0.38) {
    return {
      key: "refinance",
      label: CTA_SAVINGS_CALCULATOR,
      category: "refinance",
    };
  }
  return {
    key: "blended",
    label: CTA_SAVINGS_CALCULATOR,
    category: "insurance",
  };
}
